import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { type Chat, INPUT_ATTACHMENT_MAX_SIZE_BYTES } from '@trinity/chat';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { CustomElementRenderer } from '@trinity/custom-pages-registry';
import type { CustomElements } from '@trinity/custom-pages-registry-types';
import { AttachmentsContext } from '../Chat/context';
import { ChatMessageAttachments } from '../Chat/Message.Attachments';
import { CustomPageContextContainer } from '../CustomPageContext';
import { EntryButtons } from './DynamicForm.introduction.buttons';
import { DraftTable, type DraftPreview } from './DynamicForm.introduction.drafts';
import { UploadedFilePanel } from './DynamicForm.introduction.fileUpload';
import { Message, VoiceWarningBox } from './DynamicForm.styles.index';

const CONSTANTS = {
  DEFAULT_PROMPT: '',
  DEFAULT_WARNING: '',
} as const;

const ACCEPTED_UPLOAD_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'pptx']);
const MAX_UPLOAD_MB = Math.round(INPUT_ATTACHMENT_MAX_SIZE_BYTES / (1024 * 1024));

const sanitizeText = (value: string | undefined, fallback: string): string => {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
};

const IntroResponseSection: React.FC<{
  introResponse?: { value: string; attachments?: unknown[] };
}> = ({ introResponse }) => {
  const introAttachments = useMemo(() => {
    return (introResponse?.attachments as Chat.Attachment.Item[] | undefined) || [];
  }, [introResponse?.attachments]);

  if (!introResponse || !introResponse.value || !introResponse.value.trim()) {
    return null;
  }

  return (
    <AttachmentsContext.Provider value={introAttachments}>
      <ChatMarkdown value={introResponse.value} />
      {introResponse.attachments &&
        Array.isArray(introResponse.attachments) &&
        introResponse.attachments.length > 0 && (
          <ChatMessageAttachments attachments={introResponse.attachments as Chat.Attachment.Item[]} />
        )}
    </AttachmentsContext.Provider>
  );
};

interface DynamicFormIntroductionProps {
  onManualEntry: () => void;
  onSpeechEntry: () => void;
  onCancel: () => void;
  onLoadDraft?: (draftId: string) => void;
  prompt?: string;
  warningMessage?: string;
  showCancelButton?: boolean;
  showSpeechOption?: boolean;
  showManualButton?: boolean;
  showFileUploadOption?: boolean;
  isVoiceActive?: boolean;
  availableDrafts?: DraftPreview[];
  onViewDrafts?: () => void;
  draftOnlyMode?: boolean; // When true, only show draft options, hide new entry buttons
  introResponse?: { value: string; attachments?: unknown[] }; // Optional response content to display above form entry options
  dashboardElement?: CustomElements;
  onFileUploadProceed?: (file: File) => void;
  isFileExtracting?: boolean;
}

function computeVisibility(
  showVoiceWarning: boolean,
  isVoiceActive: boolean,
  draftOnlyMode: boolean,
  warningMessage: string | undefined,
  availableDrafts: DraftPreview[]
) {
  const shouldShowButtons = !showVoiceWarning && !isVoiceActive && !draftOnlyMode;
  const shouldShowVoiceWarning =
    (showVoiceWarning || isVoiceActive) && !!warningMessage && warningMessage.trim().length > 0;
  const shouldShowDraftTable = draftOnlyMode && availableDrafts && availableDrafts.length > 0;
  return { shouldShowButtons, shouldShowVoiceWarning, shouldShowDraftTable };
}

function IntroContent({
  validPrompt,
  shouldShowDraftTable,
  availableDrafts,
  onLoadDraft,
  shouldShowButtons,
  handlers,
  buttonProps,
  uploadedFileName,
  onUploadedFileProceed,
  isFileExtracting,
  shouldShowVoiceWarning,
  warningMessage,
}: {
  validPrompt: string;
  shouldShowDraftTable: boolean;
  availableDrafts: DraftPreview[];
  onLoadDraft?: (draftId: string) => void;
  shouldShowButtons: boolean;
  handlers: {
    handleManualEntry: () => void;
    handleSpeechEntry: () => void;
    handleCancel: () => void;
    handleFileUpload: () => void;
  };
  buttonProps: {
    showManualButton: boolean;
    showSpeechOption: boolean;
    showFileUploadOption: boolean;
    showCancelButton: boolean;
  };
  uploadedFileName: string | null;
  onUploadedFileProceed: () => void;
  isFileExtracting: boolean;
  shouldShowVoiceWarning: boolean;
  warningMessage?: string;
}) {
  return (
    <>
      {validPrompt && (
        <Message role="status" aria-live="polite">
          {validPrompt}
        </Message>
      )}

      {shouldShowDraftTable && (
        <DraftTable availableDrafts={availableDrafts} onLoadDraft={onLoadDraft || (() => {})} />
      )}

      {shouldShowButtons &&
        (uploadedFileName !== null ? (
          <UploadedFilePanel
            fileName={uploadedFileName}
            onReplace={handlers.handleFileUpload}
            onProceed={onUploadedFileProceed}
            isExtracting={isFileExtracting}
          />
        ) : (
          <EntryButtons
            onManualEntry={handlers.handleManualEntry}
            onSpeechEntry={handlers.handleSpeechEntry}
            onCancel={handlers.handleCancel}
            onFileUpload={handlers.handleFileUpload}
            showManualButton={buttonProps.showManualButton}
            showSpeechOption={buttonProps.showSpeechOption}
            showFileUploadOption={buttonProps.showFileUploadOption}
            showCancelButton={buttonProps.showCancelButton}
          />
        ))}

      {shouldShowVoiceWarning && (
        <VoiceWarningBox role="region" aria-label="Voice input instructions">
          {warningMessage}
        </VoiceWarningBox>
      )}
    </>
  );
}

export const DynamicFormIntroduction: React.FC<DynamicFormIntroductionProps> = memo(
  ({
    onManualEntry,
    onSpeechEntry,
    onCancel,
    onLoadDraft,
    prompt = CONSTANTS.DEFAULT_PROMPT,
    warningMessage,
    showCancelButton = true,
    showSpeechOption = true,
    showManualButton = true,
    showFileUploadOption = false,
    isVoiceActive = false,
    availableDrafts = [],
    draftOnlyMode = false,
    introResponse,
    dashboardElement,
    onFileUploadProceed,
    isFileExtracting = false,
  }) => {
    const [showVoiceWarning, setShowVoiceWarning] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [fileUploadError, setFileUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = useCallback(() => {
      if (fileInputRef.current) {
        // Clearing lets the same file be picked again
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
    }, []);

    const handleFileSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ACCEPTED_UPLOAD_EXTENSIONS.has(ext)) {
        setFileUploadError('Only PNG, JPG, and PPTX files are supported.');
        return;
      }
      if (file.size > INPUT_ATTACHMENT_MAX_SIZE_BYTES) {
        setFileUploadError(`File is too large. The maximum size is ${MAX_UPLOAD_MB} MB.`);
        return;
      }
      setFileUploadError(null);
      setUploadedFile(file);
    }, []);

    const handleManualEntry = useCallback(() => {
      setShowVoiceWarning(false);
      onManualEntry?.();
    }, [onManualEntry]);

    const handleSpeechEntry = useCallback(() => {
      setShowVoiceWarning(true);
      onSpeechEntry?.();
    }, [onSpeechEntry]);

    const handleCancel = useCallback(() => {
      setShowVoiceWarning(false);
      onCancel?.();
    }, [onCancel]);

    const validPrompt = useMemo(() => sanitizeText(prompt, CONSTANTS.DEFAULT_PROMPT), [prompt]);
    const { shouldShowButtons, shouldShowVoiceWarning, shouldShowDraftTable } = computeVisibility(
      showVoiceWarning,
      isVoiceActive,
      draftOnlyMode,
      warningMessage,
      availableDrafts
    );

    return (
      <>
        <IntroResponseSection introResponse={introResponse} />

        {dashboardElement != null && (
          <CustomPageContextContainer>
            <CustomElementRenderer element={dashboardElement} />
          </CustomPageContextContainer>
        )}

        <IntroContent
          validPrompt={validPrompt}
          shouldShowDraftTable={shouldShowDraftTable}
          availableDrafts={availableDrafts}
          onLoadDraft={onLoadDraft}
          shouldShowButtons={shouldShowButtons}
          handlers={{
            handleManualEntry,
            handleSpeechEntry,
            handleCancel,
            handleFileUpload,
          }}
          buttonProps={{ showManualButton, showSpeechOption, showFileUploadOption, showCancelButton }}
          uploadedFileName={showFileUploadOption ? (uploadedFile?.name ?? null) : null}
          onUploadedFileProceed={
            onFileUploadProceed != null && uploadedFile != null
              ? () => onFileUploadProceed(uploadedFile)
              : handleManualEntry
          }
          isFileExtracting={isFileExtracting}
          shouldShowVoiceWarning={shouldShowVoiceWarning}
          warningMessage={warningMessage}
        />

        {showFileUploadOption && (
          <input
            ref={fileInputRef}
            type="file"
            accept={[...ACCEPTED_UPLOAD_EXTENSIONS].map((ext) => `.${ext}`).join(',')}
            hidden
            onChange={handleFileSelected}
          />
        )}
        {fileUploadError && (
          <div role="alert" style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {fileUploadError}
          </div>
        )}
      </>
    );
  }
);

DynamicFormIntroduction.displayName = 'DynamicFormIntroduction';

export default DynamicFormIntroduction;
export type { DraftPreview } from './DynamicForm.introduction.drafts';
